import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Mail, Clock, Globe, Calendar, MessageSquare, ChevronDown,
  Zap, ArrowRight, CreditCard, Wrench, GraduationCap, UserCog,
  CheckCircle2, Send, AlertCircle, LifeBuoy,
} from 'lucide-react';

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function ContactPage() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const isRTL = isAr;

  const [formData, setFormData] = useState({
    name: '', email: '', subject: '', category: '', message: '',
  });
  const [status, setStatus] = useState<Status>('idle');
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const MAX_MESSAGE = 1000;

  const t = (en: string, ar: string) => (isAr ? ar : en);

  const categories = [
    { value: 'technical', label: t('Technical Support', 'الدعم الفني') },
    { value: 'subscription', label: t('Subscription', 'الاشتراك') },
    { value: 'billing', label: t('Billing', 'الفواتير') },
    { value: 'exam', label: t('Exam Preparation', 'التحضير للاختبار') },
    { value: 'general', label: t('General Inquiry', 'استفسار عام') },
  ];

  const badges = [
    { icon: CheckCircle2, text: t('Response within 24 hours', 'رد خلال 24 ساعة') },
    { icon: CheckCircle2, text: t('PMP-focused support', 'دعم متخصص في PMP') },
    { icon: CheckCircle2, text: t('Arabic & English', 'العربية والإنجليزية') },
    { icon: CheckCircle2, text: t('Technical Assistance', 'مساعدة فنية') },
  ];

  const supportCards = [
    { icon: Mail, label: t('Email Support', 'دعم البريد الإلكتروني'), value: 'support@pmpqandanswer.web.app', href: 'mailto:support@pmpqandanswer.web.app' },
    { icon: Clock, label: t('Average Response Time', 'متوسط وقت الاستجابة'), value: t('Less than 24 Hours', 'أقل من 24 ساعة') },
    { icon: Globe, label: t('Languages', 'اللغات'), value: t('Arabic · English', 'العربية · الإنجليزية') },
    { icon: Calendar, label: t('Business Hours', 'ساعات العمل'), value: t('Sunday – Thursday', 'الأحد – الخميس') },
  ];

  const quickHelp = [
    { icon: CreditCard, title: t('Subscription Questions', 'أسئلة الاشتراك'), desc: t('Plans, upgrades, payments', 'الخطط، الترقيات، المدفوعات') },
    { icon: Wrench, title: t('Technical Support', 'الدعم الفني'), desc: t('Bugs, loading issues, errors', 'الأخطاء، مشاكل التحميل') },
    { icon: GraduationCap, title: t('Exam Preparation', 'التحضير للاختبار'), desc: t('Study tips, readiness scores', 'نصائح الدراسة، درجات الجاهزية') },
    { icon: UserCog, title: t('Account Issues', 'مشاكل الحساب'), desc: t('Login, password, profile', 'تسجيل الدخول، كلمة المرور') },
  ];

  const faqs = [
    { q: t('How do I upgrade my subscription?', 'كيف أرقى اشتراكي؟'), a: t('Go to the Pricing page, select your desired plan, and follow the payment instructions. Your account will be upgraded instantly.', 'انتقل إلى صفحة الأسعار، اختر الخطة المطلوبة، واتبع تعليمات الدفع. سيتم ترقية حسابك فوراً.') },
    { q: t('How do I reset my password?', 'كيف أعيد تعيين كلمة المرور؟'), a: t('On the login page, click "Forgot Password". You will receive a secure reset link via email.', 'في صفحة تسجيل الدخول، انقر على "نسيت كلمة المرور". ستصلك رسالة إعادة تعيين آمنة عبر البريد الإلكتروني.') },
    { q: t('How many PMP questions are included?', 'كم عدد أسئلة PMP المتوفرة؟'), a: t('Our question bank contains thousands of PMP-style questions covering all domains: People, Process, and Business Environment.', 'بنك الأسئلة لدينا يحتوي على آلاف الأسئلة بأسلوب PMP تغطي جميع المجالات: الأشخاص، العمليات، وبيئة الأعمال.') },
    { q: t('Can I study in Arabic?', 'هل يمكنني الدراسة بالعربية؟'), a: t('Yes! All questions and explanations are available in both English and Arabic. Toggle the language from the top navigation bar.', 'نعم! جميع الأسئلة والشروحات متوفرة باللغتين الإنجليزية والعربية. بدّل اللغة من شريط التنقل العلوي.') },
    { q: t('How do readiness scores work?', 'كيف تعمل درجات الجاهزية؟'), a: t('Your readiness score is calculated based on your accuracy, volume of questions answered, and consistency across all PMP domains. Aim for 85%+ to be exam-ready.', 'يتم حساب درجة الجاهزية بناءً على دقتك، عدد الأسئلة المجابة، وثبات أدائك عبر جميع مجالات PMP. استهدف 85%+ لتكون جاهزاً للاختبار.') },
  ];

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.name.trim()) e.name = t('Name is required', 'الاسم مطلوب');
    if (!formData.email.trim()) e.email = t('Email is required', 'البريد الإلكتروني مطلوب');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = t('Invalid email format', 'صيغة بريد غير صحيحة');
    if (!formData.subject.trim()) e.subject = t('Subject is required', 'الموضوع مطلوب');
    if (!formData.category) e.category = t('Please select a category', 'يرجى اختيار فئة');
    if (!formData.message.trim()) e.message = t('Message is required', 'الرسالة مطلوبة');
    else if (formData.message.length < 10) e.message = t('Message too short (min 10 chars)', 'الرسالة قصيرة جداً (10 أحرف على الأقل)');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => { const n = { ...prev }; delete n[e.target.name]; return n; });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('sending');

    const GOOGLE_FORM_ACTION_URL =
      'https://docs.google.com/forms/d/e/1FAIpQLSdSULih-dGfzv_0QgqQ-KOPhAHanhsZkbEAQ2D_lGqhfkzjUA/formResponse';

    const formPayload = new FormData();
    formPayload.append('entry.1312427095', formData.name);
    formPayload.append('entry.861623471', formData.email);
    formPayload.append('entry.338043395', `${formData.subject} [${formData.category}]`);
    formPayload.append('entry.296003198', formData.message);

    try {
      await fetch(GOOGLE_FORM_ACTION_URL, { method: 'POST', mode: 'no-cors', body: formPayload });
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', category: '', message: '' });
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  const inputClass = "w-full px-4 py-2.5 border rounded-xl bg-background text-foreground text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";
  const errorInputClass = (field: string) => `${inputClass} ${errors[field] ? 'border-red-500' : 'border-border'}`;
  const labelClass = "block text-sm font-medium text-muted-foreground mb-1.5";

  return (
    <>
      <SEO
        title={t('Contact Us', 'اتصل بنا')}
        description={t('Contact our PMP success team for subscriptions, exam preparation, billing, or technical support.', 'تواصل مع فريق نجاح PMP للاشتراكات والتحضير للاختبار والدعم الفني.')}
        canonical="https://pmpqandanswer.web.app/contact"
      />

      <div dir={isRTL ? 'rtl' : 'ltr'}>
        {/* ═══════════════ SECTION 1: HERO ═══════════════ */}
        <section className="relative pt-20 pb-12 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-transparent" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
          <div className="relative max-w-[1400px] mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold mb-6">
              <LifeBuoy className="w-4 h-4" />
              {t('PMP Support Center', 'مركز دعم PMP')}
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-gradient-primary">
              {t('Contact Our PMP Success Team', 'تواصل مع فريق نجاح PMP')}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              {t(
                'Need help with subscriptions, exam preparation, billing, or technical support? Our team is ready to help you succeed.',
                'هل تحتاج مساعدة في الاشتراك أو التحضير للاختبار أو الدعم الفني؟ فريقنا جاهز لمساعدتك.'
              )}
            </p>
            {/* Support Badges */}
            <div className="flex flex-wrap items-center justify-center gap-3 max-w-3xl mx-auto">
              {badges.map((b, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm text-xs font-medium">
                  <b.icon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 2: TWO COLUMN LAYOUT ═══════════════ */}
        <section className="py-12 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid lg:grid-cols-[65%_35%] gap-6">
              {/* LEFT: Contact Form */}
              <div className="glass-card rounded-2xl border border-border/50 p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-bold mb-1">{t('Send Us a Message', 'أرسل لنا رسالة')}</h2>
                <p className="text-sm text-muted-foreground mb-6">{t('Fill out the form below and we\'ll get back to you within 24 hours.', 'املأ النموذج أدناه وسنعاود الاتصال بك خلال 24 ساعة.')}</p>

                {status === 'success' && (
                  <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-emerald-700 dark:text-emerald-400">{t('Message Sent!', 'تم إرسال الرسالة!')}</p>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400/80">{t('Thank you! Your message has been sent successfully. We\'ll respond within 24 hours.', 'شكراً لك! تم إرسال رسالتك بنجاح. سنرد خلال 24 ساعة.')}</p>
                    </div>
                  </div>
                )}

                {status === 'error' && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700 dark:text-red-400">{t('Something went wrong.', 'حدث خطأ ما.')}</p>
                      <p className="text-sm text-red-600 dark:text-red-400/80">{t('Please try again or email us directly.', 'يرجى المحاولة مرة أخرى أو راسلنا مباشرة.')}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className={labelClass}>{t('Name', 'الاسم')}</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={errorInputClass('name')} placeholder={t('Your name', 'اسمك')} />
                      {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className={labelClass}>{t('Email', 'البريد الإلكتروني')}</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={errorInputClass('email')} placeholder="your.email@example.com" />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className={labelClass}>{t('Subject', 'الموضوع')}</label>
                    <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} className={errorInputClass('subject')} placeholder={t('Brief subject of your inquiry', 'موضوع موجز لاستفسارك')} />
                    {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
                  </div>

                  <div>
                    <label htmlFor="category" className={labelClass}>{t('Category', 'الفئة')}</label>
                    <select id="category" name="category" value={formData.category} onChange={handleChange} className={errorInputClass('category')}>
                      <option value="">{t('Select a category...', 'اختر فئة...')}</option>
                      {categories.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
                  </div>

                  <div>
                    <label htmlFor="message" className={labelClass}>{t('Message', 'الرسالة')}</label>
                    <textarea
                      id="message" name="message" value={formData.message} onChange={handleChange}
                      rows={6} maxLength={MAX_MESSAGE}
                      className={`${errorInputClass('message')} resize-none`}
                      placeholder={t('Tell us about your inquiry in detail...', 'أخبرنا عن استفسارك بالتفصيل...')}
                    />
                    <div className="flex items-center justify-between mt-1">
                      {errors.message ? <p className="text-xs text-red-500">{errors.message}</p> : <span />}
                      <span className="text-xs text-muted-foreground">{formData.message.length}/{MAX_MESSAGE}</span>
                    </div>
                  </div>

                  <button
                    type="submit" disabled={status === 'sending'}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-bold text-sm hover:scale-[1.02] transition-all shadow-lg hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'sending' ? (
                      <><span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />{t('Sending...', 'جاري الإرسال...')}</>
                    ) : (
                      <><Send className="w-4 h-4" />{t('Send Message', 'إرسال الرسالة')}</>
                    )}
                  </button>
                </form>
              </div>

              {/* RIGHT: Support Info Panel */}
              <div className="space-y-4">
                <div className="glass-card rounded-2xl border border-border/50 p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <LifeBuoy className="w-5 h-5 text-primary" />
                    {t('Support Information', 'معلومات الدعم')}
                  </h3>
                  <div className="space-y-4">
                    {supportCards.map((card, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <card.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground mb-0.5">{card.label}</p>
                          {card.href ? (
                            <a href={card.href} className="text-sm font-semibold hover:text-primary transition-colors break-all">{card.value}</a>
                          ) : (
                            <p className="text-sm font-semibold">{card.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FAQ Shortcut */}
                <div className="glass-card rounded-2xl border border-border/50 p-6 text-center shadow-sm bg-gradient-to-br from-primary/5 to-purple-500/5">
                  <MessageSquare className="w-8 h-8 text-primary mx-auto mb-3" />
                  <p className="text-sm font-bold mb-1">{t('Looking for quick answers?', 'تبحث عن إجابات سريعة؟')}</p>
                  <p className="text-xs text-muted-foreground mb-4">{t('Browse our FAQ section below', 'تصفح قسم الأسئلة الشائعة أدناه')}</p>
                  <a href="#faq" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-accent transition-all">
                    {t('View Help Center', 'مركز المساعدة')}
                    <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 3: QUICK HELP ═══════════════ */}
        <section className="py-16 px-4 bg-gradient-to-br from-primary/5 to-purple-500/5">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-2">{t('Need Answers Faster?', 'تحتاج إجابات أسرع؟')}</h2>
              <p className="text-muted-foreground text-sm">{t('Browse common topics to find what you need instantly', 'تصفح المواضيع الشائعة للعثور على ما تحتاجه فوراً')}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickHelp.map((item, i) => (
                <a
                  key={i} href="#faq"
                  className="glass-card rounded-2xl border border-border/50 p-6 hover:shadow-premium-lg transition-all hover:scale-[1.02] group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-purple-500/15 flex items-center justify-center mb-4 group-hover:from-primary/25 group-hover:to-purple-500/25 transition-all">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 4: FAQ ═══════════════ */}
        <section id="faq" className="py-20 px-4 scroll-mt-20">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-2">{t('Frequently Asked Questions', 'الأسئلة الشائعة')}</h2>
              <p className="text-muted-foreground text-sm">{t('Everything you need to know about the platform', 'كل ما تحتاج معرفته عن المنصة')}</p>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <div key={i} className="glass-card rounded-xl border border-border/50 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-start hover:bg-accent/30 transition-colors"
                  >
                    <span className="text-sm font-semibold">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed animate-fade-in">{f.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 5: FINAL CTA ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="relative rounded-3xl bg-gradient-to-br from-primary via-purple-600 to-purple-700 p-12 md:p-16 text-center overflow-hidden">
              <div className="absolute inset-0 bg-grid-pattern opacity-10" />
              <div className="relative">
                <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
                  {t('Ready to Continue Your PMP Journey?', 'جاهز لمواصلة رحلتك نحو شهادة PMP؟')}
                </h2>
                <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                  {t('Start practicing with thousands of PMP-style questions today.', 'ابدأ التدريب مع آلاف الأسئلة بأسلوب PMP اليوم.')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/exam" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-primary font-bold text-lg hover:scale-[1.02] transition-all shadow-xl">
                    <Zap className="w-5 h-5" />
                    {t('Start Practice Now', 'ابدأ التدريب الآن')}
                    <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </Link>
                  <Link to="/dashboard" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-white/30 text-white font-bold text-lg hover:bg-white/10 transition-all">
                    {t('View Dashboard', 'عرض لوحة التحكم')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}